import React, { useEffect, useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Card,
  CardContent,
  Grid,
  CardHeader,
  Box,
} from "@mui/material";
import { BookingApi } from "../../API/Apis/BookingApi";
import { t } from "i18next";
import DefaultPage from "../../components/Layouts/DefaultPage";
import AlertMessage from "../AlertMessage";
import Pagination from "../../components/Layouts/ReservationsCard/Pagination";

// Extracted components
const InvoiceTable = ({ displayedInvoices, downloadInvoice, t }) => (
  <TableContainer className="!shadow-grey-400 !rounded-2xl !border-solid !border-3 p-4">
    <Table
      className="!shadow-2xl rounded-2xl !border-solid !border-2 !border-grey-400"
      stickyHeader
    >
      <TableHead
        className="!bg-[primary] rounded-2xl"
        style={{ backgroundColor: "#BDBDBD" }}
      >
        <TableRow>
          {["Invoice Number", "Date", "Amount", "Status", "Download"].map(
            (header) => (
              <TableCell
                key={header}
                className="!text-[15px]"
                style={{ backgroundColor: "#BDBDBD", color: "#000000" }}
              >
                {t(header)}
              </TableCell>
            )
          )}
        </TableRow>
      </TableHead>
      <TableBody>
        {displayedInvoices.map((e, i) => {
          return (
            <InvoiceTableRow
              key={i}
              e={e}
              downloadInvoice={downloadInvoice}
              t={t}
            />
          );
        })}
      </TableBody>
    </Table>
  </TableContainer>
);

const formattedInvoiceDate = (isoDate) => {
  if (!isoDate) return "";
  return new Date(isoDate).toLocaleDateString("en-GB");
};

const InvoiceTableRow = ({ e, downloadInvoice, t }) => (
  <TableRow>
    <TableCell
      className="!text-[12px] text-[#333] rounded-bl-2xl"
      component="th"
    >
      <InvoiceNumberCell e={e} />
    </TableCell>
    <TableCell className="!text-[12px] text-[#333]">
      {formattedInvoiceDate(e.invoiceDate)}
    </TableCell>
    <TableCell className="!text-[12px] text-[#333]">
      {"€" + " " + e.totalnvoice || ""}
    </TableCell>
    <TableCell>
      <StatusCell e={e} />
    </TableCell>
    <TableCell>
      <DownloadButton e={e} t={t} downloadInvoice={downloadInvoice} />
    </TableCell>
  </TableRow>
);

const InvoiceNumberCell = ({ e }) => {
  return (
    <ul>
      <li>{e.prefixNo || ""}</li>
      <li className="text-[#838181] text-[10px]">
        {"Reservation " + e.reservation}
      </li>
    </ul>
  );
};

const StatusCell = ({ e }) => (
  <div
    className="!text-[11px] text-[#333]"
    style={{
      backgroundColor: e.isPaid ? "lightgreen" : "#FF634748",
      color: e.isPaid ? "#008741" : "primary",
      textAlign: "center",
      borderRadius: "1rem",
      fontWeight: "bold",
      padding: "2px",
    }}
  >
    {e.isPaid ? "Paid" : "Unpaid"}
  </div>
);

const DownloadButton = ({ e, t, downloadInvoice }) => (
  <button
    className="flex text-[14px] h-70 text-[#FF0000] rounded"
    onClick={() => downloadInvoice(e.id, e.idSupplier)}
  >
    {t("Invoice")}
    <img
      className="h-3 ml-[5px]"
      src="/assets/images/downloadIcon.png"
      alt="Invoice Icon"
    />
  </button>
);

const MobileInvoiceCard = ({ e, t, downloadInvoice }) => (
  <Card className="!shadow-2xl rounded-2xl !border-solid !border-2 !border-grey-400 w-full whitespace-nowrap">
    <CardHeader
      title={`${e.prefix || ""} ${e.invoiceNumber || ""}`}
      style={{
        color: "#000000",
        backgroundColor: "#BDBDBD",
        height: "30px",
        padding: "8px 16px",
      }}
      titleTypographyProps={{ style: { color: "#000000", fontSize: "14px" } }}
    />
    <CardContent style={{ padding: "8px 16px" }}>
      {[
        {
          label: "Customer Name",
          value: `${e.customer.firstName} ${e.customer.lastName}`,
        },
        {
          label: "Email Address",
          value: e.customer.email,
          style: { color: "#7B7B7B" },
        },
        { label: "Date", value: formattedInvoiceDate(e.invoiceDate) },
        { label: "Amount", value: `€${e.totalnvoice}` },
        { label: "Status", component: <MobileStatusCell e={e} /> },
        {
          label: "Download Invoice",
          component: (
            <MobileDownloadButton
              e={e}
              t={t}
              downloadInvoice={downloadInvoice}
            />
          ),
        },
      ].map((item, index) => (
        <React.Fragment key={index}>
          <MobileCardRow
            label={item.label}
            value={item.value}
            style={item.style}
            component={item.component}
          />
          {index < 5 && <hr />}
        </React.Fragment>
      ))}
    </CardContent>
  </Card>
);

const MobileCardRow = ({ label, value, style, component }) => (
  <Typography
    style={{
      display: "flex",
      justifyContent: "space-between",
      fontSize: "14px",
      marginBottom: "6px",
      marginTop: "6px",
    }}
  >
    <span>{t(label)}</span>
    {component || <span style={style}>{value}</span>}
  </Typography>
);

const MobileStatusCell = ({ e }) => (
  <span
    className="!text-[12px]"
    style={{
      backgroundColor: e.isPaid ? "lightgreen" : "#FF634748",
      color: e.isPaid ? "#008741" : "#A90D00",
      textAlign: "center",
      borderRadius: "0.3rem",
      width: "18%",
      marginTop: "1%",
    }}
  >
    {e.isPaid ? "Paid" : "Unpaid"}
  </span>
);

const MobileDownloadButton = ({ e, t, downloadInvoice }) => (
  <button
    className="flex items-center border border-gray-400 text-[14px] text-black rounded px-2 mt-1 hover:bg-gray-200"
    onClick={() => downloadInvoice(e.id, e.idSupplier)}
    style={{ height: "17px", fontSize: "12px" }}
  >
    {t("Invoice")}
    <img
      className="h-3 ml-2"
      src="/assets/images/downloadIcon.png"
      alt="Invoice Icon"
    />
  </button>
);

/*
const formatDate = (dateString) => {
  const date = new Date(dateString);
  return `${String(date.getDate()).padStart(2, "0")}-${String(
    date.getMonth() + 1
  ).padStart(2, "0")}-${date.getFullYear()}`;
};
*/

// Main component
export default function InvoiceCard(props) {
  const [invoiceList, setInvoiceList] = useState([]);
  const [invoiceFilterList, setInvoiceFilterList] = useState([]);
  const [loader, setLoader] = useState(true);
  const [page, setPage] = useState(0);
  const [rowsPerPage] = useState(6);
  const [totalInvoices, setTotalInvoices] = useState(0);
  const [openModal, setOpenModal] = useState(false);
  const [modalMessage, setModalMessage] = useState("");
  const isSmallScreen = window.innerWidth <= 768;
  const requestOnClose = () => {
    setOpenModal(false);
    setModalMessage("");
  };
  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  // const handleRowsPerPage = (event) => {
  //   setRowsPerPage(+event.target.value);
  //   setPage(0);
  // };

  const downloadInvoice = (invoiceId, idSupplier) => {
    if (invoiceId == "No Invoice Found") {
      setModalMessage("No Invoice Found");
      setOpenModal(true);
    } else {
      BookingApi.downloadInvoice(invoiceId, idSupplier).then((e) => {
        if (e.data.status == "error") {
          setModalMessage(e.data.errorMessage);
          setOpenModal(true);
        } else {
          window.open(e.data, "_blank");
        }
      });
    }
  };

  useEffect(() => {
    if (props.searchInvoice) {
      console.log("props.searchInvoice", props.searchInvoice);
      const searchTerm = props.searchInvoice
        .trim()
        .replace(/\s+/g, "")
        .toLowerCase();
      const filteredInvoices = invoiceFilterList.filter((invoice) => {
        console.log("invoice", invoice);
        const prefixNo = invoice?.prefixNo?.toLowerCase() || "";
        console.log("prefixNo", prefixNo);
        return prefixNo.includes(searchTerm);
      });
      setTotalInvoices(filteredInvoices.length);
      setInvoiceList(filteredInvoices);
    } else {
      setTotalInvoices(invoiceList.length);
      setInvoiceList(invoiceList);
    }
  }, [props.searchInvoice]);

  useEffect(() => {
    setLoader(true);
    const id = localStorage.getItem("userID");
    BookingApi.getOnlineUserInvoices(id, page * rowsPerPage, rowsPerPage).then(
      (e) => {
        setTotalInvoices(e.data.total);
        setInvoiceList(e.data.invoiceList);
        setInvoiceFilterList(e.data.invoiceList);
        setLoader(false);
      }
    );
  }, [page, rowsPerPage]);

  if (loader) {
    return (
      <Typography className="!text-center !text-3xl !flex !justify-center">
        Loading...
      </Typography>
    );
  }

  if (!invoiceList || invoiceList.length === 0) {
    return <DefaultPage message={t("You haven't created any invoices yet.")} />;
  }

  return (
    <>
      <AlertMessage
        openModal={openModal}
        requestOnClose={requestOnClose}
        modalMessage={modalMessage}
        isTermsConditionPopUp={false}
      />

      {!isSmallScreen ? (
        <>
          <InvoiceTable
            displayedInvoices={invoiceList}
            downloadInvoice={downloadInvoice}
            t={t}
          />
          <div style={{ display: "flex", justifyContent: "center" }}>
            <Pagination
              count={totalInvoices}
              page={page}
              rowsPerPage={rowsPerPage}
              onPageChange={handleChangePage}
              //onRowsPerPageChange={handleChangeRowsPerPage}
            />
          </div>
        </>
      ) : (
        <>
          <Grid container spacing={2}>
            {invoiceList.map((e, i) => (
              <Grid item xs={12} sm={6} md={4} key={i}>
                <MobileInvoiceCard
                  e={e}
                  t={t}
                  downloadInvoice={downloadInvoice}
                />
              </Grid>
            ))}
          </Grid>
          <Box display="flex" justifyContent="center" mt={4}>
            <Pagination
              count={totalInvoices}
              page={page}
              rowsPerPage={rowsPerPage}
              onPageChange={handleChangePage}
            />
          </Box>
        </>
      )}
    </>
  );
}
